// Utility functions for formatting data in the frontend

/**
 * Format a token amount with specified decimal places
 */
export function formatTokenAmount(amount: string | number, decimals: number = 2): string {
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  if (isNaN(numAmount)) return '0';
  
  if (numAmount >= 1e9) {
    return (numAmount / 1e9).toFixed(decimals) + 'B';
  }
  if (numAmount >= 1e6) {
    return (numAmount / 1e6).toFixed(decimals) + 'M';
  }
  if (numAmount >= 1e3) {
    return (numAmount / 1e3).toFixed(decimals) + 'K';
  }
  
  return numAmount.toFixed(decimals);
}

/**
 * Format a number with thousand separators
 */
export function formatNumber(num: number): string {
  if (isNaN(num)) return '0';
  return num.toLocaleString();
}

/**
 * Format a percentage value
 */
export function formatPercentage(value: number, decimals: number = 1): string {
  if (isNaN(value)) return '0%';
  return `${value.toFixed(decimals)}%`;
}

/**
 * Format duration in milliseconds to human readable format
 */
export function formatDuration(milliseconds: number): string {
  if (isNaN(milliseconds) || milliseconds < 0) return '0ms';
  
  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  
  if (days > 0) {
    return `${days}d ${hours % 24}h`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  }
  if (seconds > 0) {
    return `${seconds}s`;
  }
  
  return `${milliseconds}ms`;
}

/**
 * Format a timestamp to human readable date
 */
export function formatDate(timestamp: number | string | Date): string {
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return 'Invalid Date';
  
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

/**
 * Format a relative time (e.g., "2 hours ago")
 */
export function formatRelativeTime(timestamp: number | string | Date): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  
  if (isNaN(diffMs)) return 'Invalid Date';
  
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffDays > 0) {
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  }
  if (diffHours > 0) {
    return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  }
  if (diffMinutes > 0) {
    return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
  }
  if (diffSeconds > 10) {
    return `${diffSeconds} second${diffSeconds > 1 ? 's' : ''} ago`;
  }
  
  return 'Just now';
}

/**
 * Truncate a string to specified length with ellipsis
 */
export function truncateString(str: string, maxLength: number): string {
  if (str.length <= maxLength) return str;
  return str.substring(0, maxLength - 3) + '...';
}

/**
 * Format an Ethereum address with ellipsis in the middle
 */
export function formatAddress(address: string, startChars: number = 6, endChars: number = 4): string {
  if (!address || address.length < startChars + endChars) return address;
  return `${address.substring(0, startChars)}...${address.substring(address.length - endChars)}`;
}

/**
 * Combine multiple CSS class names
 */
export function clsx(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ');
}

/**
 * Generate explorer URL for a transaction based on chain ID
 */
export function getExplorerUrl(chainId: string, txHash: string): string {
  const bscExplorer = import.meta.env.VITE_BSC_CHAIN_EXPLORER || 'https://bscscan.com';
  const roninExplorer = import.meta.env.VITE_RONIN_CHAIN_EXPLORER || 'https://app.roninchain.com';
  
  if (chainId === '56' || chainId === '97') {
    return `${bscExplorer}/tx/${txHash}`;
  } else if (chainId === '2020' || chainId === '2021') {
    return `${roninExplorer}/tx/${txHash}`;
  }
  
  // Default fallback
  return `https://bscscan.com/tx/${txHash}`;
}

/**
 * Get chain name from chain ID
 */
export function getChainName(chainId: string): string {
  switch (chainId) {
    case '56':
    case '97':
      return 'BSC';
    case '2020':
    case '2021':
      return 'Ronin';
    default:
      return 'Unknown';
  }
}

/**
 * Get explorer name from chain ID
 */
export function getExplorerName(chainId: string): string {
  switch (chainId) {
    case '56':
    case '97':
      return 'BSCScan';
    case '2020':
    case '2021':
      return 'Ronin Explorer';
    default:
      return 'Explorer';
  }
}